import { api } from "./client";
import type { components } from "./types";

export type Unit = components["schemas"]["Unit"];
export type UnitDetail = components["schemas"]["UnitDetail"];
export type UnitLead = components["schemas"]["UnitLead"];
export type UnitLoadDay = components["schemas"]["UnitLoadDay"];
export type UnitLoadByProjectDay = components["schemas"]["UnitLoadByProjectDay"];
export type UnitLoadByProjectEntry = components["schemas"]["UnitLoadByProjectEntry"];
export type UnitCapacityTimelineDay = components["schemas"]["UnitCapacityTimelineDay"];
export type UnitCreateRequest = components["schemas"]["UnitCreateRequest"];
export type UnitPatchRequest = components["schemas"]["UnitPatchRequest"];
export type UnitMoveRequest = components["schemas"]["UnitMoveRequest"];
export type UserLookup = components["schemas"]["UserLookup"];
export type UserRole = components["schemas"]["UserRole"];
export type UnitApprovalMode = components["schemas"]["UnitApprovalMode"];
export type UserLookupPurpose = "project_manager";

// `ListUnitsParams` previously carried `includeInactive` — Unit.is_active was
// removed in PR22, so the call no longer takes flags. Kept as `Record<string,
// never>` so callers passing `{}` still type-check.
export type ListUnitsParams = Record<string, never>;

export function listUnits(): Promise<Unit[]> {
  return api.get<Unit[]>(`/units`);
}

export function listUsersLookup(
  options: { role?: UserRole; purpose?: UserLookupPurpose } = {},
): Promise<UserLookup[]> {
  const query = new URLSearchParams();
  if (options.role) query.set("role", options.role);
  if (options.purpose) query.set("purpose", options.purpose);
  const tail = query.size > 0 ? `?${query.toString()}` : "";
  return api.get<UserLookup[]>(`/users/lookup${tail}`);
}

export function getUnit(id: number): Promise<UnitDetail> {
  return api.get<UnitDetail>(`/units/${id}`);
}

export interface UnitDateRange {
  from: string;
  to: string;
}

export function getUnitLoad(id: number, range: UnitDateRange): Promise<UnitLoadDay[]> {
  const qs = new URLSearchParams({ from: range.from, to: range.to });
  return api.get<UnitLoadDay[]>(`/units/${id}/load?${qs.toString()}`);
}

export function getUnitLoadByProject(
  id: number,
  range: UnitDateRange,
): Promise<UnitLoadByProjectDay[]> {
  const qs = new URLSearchParams({ from: range.from, to: range.to });
  return api.get<UnitLoadByProjectDay[]>(`/units/${id}/load-by-project?${qs.toString()}`);
}

export function createUnit(body: UnitCreateRequest): Promise<Unit> {
  return api.post<Unit>(`/units`, body);
}

export function patchUnit(id: number, body: UnitPatchRequest): Promise<Unit> {
  return api.patch<Unit>(`/units/${id}`, body);
}

export function moveUnit(id: number, body: UnitMoveRequest): Promise<Unit> {
  return api.post<Unit>(`/units/${id}/move`, body);
}

/**
 * Switch a unit's time-approval mode (task 116 §1.1). Separate from `patchUnit`
 * (admin-only) — this endpoint is gated by «acts on the unit», so a lead may
 * flip their own unit while the rest of unit management stays admin-only.
 */
export function setUnitApprovalMode(id: number, mode: UnitApprovalMode): Promise<Unit> {
  return api.patch<Unit>(`/units/${id}/approval-mode`, { approval_mode: mode });
}

export function deleteUnit(id: number): Promise<void> {
  return api.delete<void>(`/units/${id}`);
}

/** Blocking counts returned in a 409 when a unit still has members or tasks. */
export interface UnitDeleteBlock {
  children: number;
  members: number;
  tasks: number;
}

export function parseUnitDeleteBlock(body: unknown): UnitDeleteBlock | null {
  if (typeof body !== "object" || body === null) return null;
  const b = body as Record<string, unknown>;
  if (
    typeof b.members === "number" &&
    typeof b.tasks === "number" &&
    typeof b.children === "number"
  ) {
    return { children: b.children, members: b.members, tasks: b.tasks };
  }
  return null;
}

export function getUnitCapacityTimeline(
  id: number,
  range: UnitDateRange,
  subtree = false,
): Promise<UnitCapacityTimelineDay[]> {
  const qs = new URLSearchParams({ from: range.from, to: range.to });
  if (subtree) qs.set("subtree", "true");
  return api.get<UnitCapacityTimelineDay[]>(`/units/${id}/capacity-timeline?${qs.toString()}`);
}
